import React, { use, useCallback, useEffect, useMemo, useState } from "react";
import {
  Stack,
  Box,
  Typography,
  Chip,
  Divider,
  Collapse,
  IconButton,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Button,
  CircularProgress,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { useCartStore } from "../store/useCartStore";
import { useCheckoutFlowStore } from "../store/useCheckoutFlowStore";
import { useAuthStore, User } from "../store/useAuthStore";
import { useLocation } from "react-router-dom";
import { elevation } from "../styles/shadows";
import FormKYC from "../components/forms/FormKYC";
import FormContact from "../components/forms/FormContact";
import { useCart } from "../hooks/useCart";

import { setLastVisitPath } from "../service/setLastPath";
import { KycFormInput } from "../types/kyc";
import { CheckoutStep } from "../types/common";
import PaymentStage from "./PaymentStage";
import LoadingScreen from "../components/LoadingScreen";
import { useOrder } from "../hooks/useOrder";
import { useKyc } from "../hooks/useKyc";
import { useSyncStep } from "../hooks/useSyncStep";

// only loading cart summary on currentStep === "payment"
const CartSummary = () => {
  const { isAuthenticated, user } = useAuthStore();
  const { currentStep } = useCheckoutFlowStore();

  const isUsingApiCart = isAuthenticated && currentStep === "payment";

  const localCartItems = useCartStore((state) => state.cartItems);
  const getTotalQuantity = useCartStore((state) => state.getTotalQuantity);
  const getTotalPrice = useCartStore((state) => state.getTotalPrice);

  const apiCart = useCart(user?.cusId || "");
  const isLoading = isUsingApiCart && apiCart.isLoading;
  const cartItems = isUsingApiCart ? apiCart.rawItems : localCartItems;
  const totalItems = isUsingApiCart
    ? apiCart.getTotalQuantity()
    : getTotalQuantity();
  const totalPrice = isUsingApiCart ? apiCart.getTotalPrice() : getTotalPrice();

  const [isOpen, setIsOpen] = useState(false);
  const hasItems = (cartItems?.length ?? 0) > 0;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.default",
        position: { lg: "sticky" },
        top: 24,
        flexShrink: 0,
        overflow: "hidden",
      }}
    >
      {/* Header doubles as the toggle — total stays visible without expanding */}
      <Box
        component="button"
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        sx={{
          all: "unset",
          boxSizing: "border-box",
          cursor: "pointer",
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: { xs: 2, sm: 2.5 },
          py: 1.75,
        }}
      >
        <ExpandMoreIcon
          sx={{
            color: "text.secondary",
            fontSize: 20,
            transition: "transform .25s ease",
            transform: isOpen ? "rotate(180deg)" : "none",
          }}
        />
        <Box sx={{ flexGrow: 1, minWidth: 0, textAlign: "left" }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ letterSpacing: "-0.01em", lineHeight: 1.2 }}
          >
            Order Summary
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {totalItems} item{totalItems !== 1 ? "s" : ""}
          </Typography>
        </Box>
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{ letterSpacing: "-0.02em", whiteSpace: "nowrap" }}
        >
          {!isLoading ? totalPrice?.toLocaleString() : "—"}{" "}
          <Box
            component="span"
            sx={{ color: "text.secondary", fontSize: "0.8rem", fontWeight: 500 }}
          >
            ₭
          </Box>
        </Typography>
      </Box>

      {/* Itemized breakdown */}
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <Divider />
        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1 }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : !hasItems ? (
            <Typography
              variant="body2"
              sx={{ textAlign: "center", color: "text.secondary", py: 3 }}
            >
              Your cart is empty
            </Typography>
          ) : (
            <Stack divider={<Divider flexItem />}>
              {cartItems?.map((item) => (
                <Box
                  key={item.packageId}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 2,
                    py: 1.25,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ lineHeight: 1.4 }}
                    >
                      {item.packageName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Qty {item.quantity}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {(
                      (item.quantity ?? 1) * Number(item.price) || 0
                    ).toLocaleString()}{" "}
                    ₭
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

const CheckoutStepper = () => {
  const currentStep = useCheckoutFlowStore((state) => state.currentStep);
  const isEmailVerified = useCheckoutFlowStore(
    (state) => state.isEmailVerified,
  );
  const isKycVerified = useCheckoutFlowStore((state) => state.isKycVerified);

  const steps: { key: CheckoutStep; label: string; completed: boolean }[] = [
    { key: "select", label: "Select", completed: true },
    { key: "contact", label: "Contact", completed: isEmailVerified },
    { key: "kyc", label: "Verify", completed: isKycVerified },
    { key: "payment", label: "Payment", completed: false },
  ];

  const activeStepIndex = Math.max(
    0,
    steps.findIndex((step) => step.key === currentStep),
  );
  const activeStep = steps[activeStepIndex];

  return (
    <Box sx={{ mb: 3 }}>
      {/* Current step + position — the only label that shows on small screens */}
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{ letterSpacing: "-0.01em" }}
        >
          {activeStep.label}
        </Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Step {activeStepIndex + 1} of {steps.length}
        </Typography>
      </Box>

      {/* Segmented progress bar */}
      <Box sx={{ display: "flex", gap: 0.75 }}>
        {steps.map((step, index) => {
          const filled = step.completed || index <= activeStepIndex;
          return (
            <Box
              key={step.key}
              sx={{
                flex: 1,
                height: 4,
                borderRadius: 999,
                bgcolor: filled ? "primary.main" : "divider",
                transition: "background-color .3s ease",
              }}
            />
          );
        })}
      </Box>

      {/* Full labels — only worth the space on larger screens */}
      <Box
        sx={{
          display: { xs: "none", sm: "flex" },
          justifyContent: "space-between",
          mt: 1,
        }}
      >
        {steps.map((step, index) => {
          const align =
            index === 0
              ? "flex-start"
              : index === steps.length - 1
                ? "flex-end"
                : "center";
          return (
            <Box
              key={step.key}
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: align,
                gap: 0.5,
              }}
            >
              {step.completed && (
                <CheckCircleIcon color="success" sx={{ fontSize: 14 }} />
              )}
              <Typography
                variant="caption"
                sx={{
                  color:
                    index === activeStepIndex ? "text.primary" : "text.secondary",
                  fontWeight: index === activeStepIndex ? 700 : 500,
                }}
              >
                {step.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const StepContent = () => {
  const { user, isAuthenticated } = useAuthStore();

  const { order } = useOrder(user?.cusId);
  const currentStep = useCheckoutFlowStore((state) => state.currentStep);
  const validateCurrentStep = useCheckoutFlowStore(
    (state) => state.validateCurrentStep,
  );

  const goToStep = useCheckoutFlowStore((state) => state.goToStep);
  const syncStep = useSyncStep();
  const { kycStatus, isKycStatusLoading } = useKyc(user?.cusId);

  const {
    kycInfo,
    isKycVerified,
    isEmailVerified,
    contactInfo,
    kycFormData,
    setKycFormData,
    setContactInfo,
    setKycFormValidation,
  } = useCheckoutFlowStore();

  // Validate current step and redirect if necessary
  useEffect(() => {
    const validation = validateCurrentStep();
    if (!validation.isValid && validation.redirectTo) {
      console.warn(
        `Redirecting from ${currentStep} to ${validation.redirectTo}: ${validation.reason}`,
      );
      goToStep(validation.redirectTo);
    }
  }, [
    currentStep,
    isEmailVerified,
    isKycVerified,
    validateCurrentStep,
    goToStep,
  ]);
  const emptyForm: KycFormInput = {
    name: "",
    surname: "",
    birth_day: "",
    gender: "",
    country: "",
    passport_no: "",
    issue_date: "",
    expiry_date: "",
    nation: "",
    doc_photo: null,
    doc_photo_url: "",
    job_id: "",
    // create-kyc no longer accepts personal_photo (field kept on form type)
    personal_photo: null,
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      const fetchingKyc = kycStatus?.isKycVerified;
      fetchingKyc !== undefined &&
        fetchingKyc !== isKycVerified &&
        goToStep("kyc");
    }
  }, []);

  // If the user is already KYC verified, skip the KYC page and jump to payment
  useEffect(() => {
    if (
      currentStep === "kyc" &&
      isKycVerified &&
      isEmailVerified &&
      !isKycStatusLoading
    ) {
      void syncStep("payment");
    }
  }, [currentStep, isKycVerified, isEmailVerified, isKycStatusLoading, syncStep]);

  // Initialize KYC form data once when entering KYC step
  useEffect(() => {
    if (currentStep === "kyc" && !kycFormData) {
      if (isKycStatusLoading) return;

      const initialData = kycInfo
        ? {
            name: kycInfo.firstname || "",
            surname: kycInfo.lastname || "",
            birth_day: kycInfo.dob || "",
            gender: kycInfo.gender || "",
            country: kycInfo.passportCountry || "",
            passport_no: kycInfo.passportNumber || "",
            issue_date: kycInfo.passportExpiryDate || "",
            expiry_date: kycInfo.passportExpiryDate || "",
            nation: kycInfo.nation || "",
            doc_photo: null,
            doc_photo_url: "",
            job_id: "",
            personal_photo: null,
          }
        : emptyForm;

      setKycFormData(initialData);
    }
  }, [currentStep, kycFormData, kycInfo, setKycFormData]);

  const handleKycChange = useCallback(
    (field: keyof KycFormInput, value: any) => {
      if (kycInfo) return;

      const current = useCheckoutFlowStore.getState().kycFormData;
      if (!current) return;

      setKycFormData({
        ...current,
        [field]: value,
      });
    },
    [setKycFormData, kycInfo],
  );

  const handleKycValidationChange = useCallback(
    (isValid: boolean, errors: Record<string, string>) => {
      setKycFormValidation(isValid, errors);
    },
    [setKycFormValidation],
  );

  const handleContactChange = useCallback(
    (field: keyof typeof contactInfo, value: string) => {
      setContactInfo({ [field]: value });
    },
    [setContactInfo],
  );

  const panelSx = {
    p: { xs: 3, sm: 4 },
    borderRadius: 2,
    border: "1px solid",
    borderColor: "divider",
    bgcolor: "background.default",
  } as const;

  const alertSx = { borderRadius: 1.5 } as const;

  // Check if current step is valid before rendering content
  const stepValidation = validateCurrentStep();
  if (!stepValidation.isValid) {
    return (
      <Paper elevation={0} sx={panelSx}>
        <Alert severity="warning" sx={alertSx}>
          {stepValidation.reason || "Validating current step..."}
        </Alert>
      </Paper>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case "contact":
        return (
          <Paper elevation={0} sx={panelSx}>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{ letterSpacing: "-0.01em", mb: 0.5 }}
            >
              Contact Information
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please provide your contact details to proceed with the booking.
            </Typography>

            {isEmailVerified && (
              <Alert severity="success" sx={{ ...alertSx, mb: 3 }}>
                Contact information verified successfully! You can proceed to
                the next step.
              </Alert>
            )}

            <FormContact
              user={user}
              contactInfo={contactInfo}
              onChange={handleContactChange}
              isEmailVerified={isEmailVerified}
            />
          </Paper>
        );

      case "kyc":
        if (!isAuthenticated) {
          return (
            <Paper elevation={0} sx={panelSx}>
              <Alert severity="warning" sx={alertSx}>
                Please log in to continue with identity verification.
              </Alert>
            </Paper>
          );
        }

        // Check if user has completed contact requirements
        if (!isEmailVerified) {
          return (
            <Paper elevation={0} sx={panelSx}>
              <Alert severity="warning" sx={alertSx}>
                Please complete and verify your contact information before
                proceeding to identity verification.
              </Alert>
            </Paper>
          );
        }

        if (isKycVerified) {
          return (
            <Paper elevation={0} sx={panelSx}>
              <Alert severity="success" sx={alertSx}>
                Your identity has been verified successfully! You can proceed to
                payment.
              </Alert>
            </Paper>
          );
        }

        // Don't render the form until kycFormData is initialized
        if (!kycFormData) {
          return (
            <Paper elevation={0} sx={panelSx}>
              <Typography color="text.secondary">
                Loading verification form...
              </Typography>
            </Paper>
          );
        }

        return (
          <Paper elevation={0} sx={panelSx}>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{ letterSpacing: "-0.01em", mb: 0.5 }}
            >
              Identity Verification
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              We need to verify your identity to comply with regulations.
            </Typography>
            <FormKYC
              formData={kycFormData}
              disableAll={false}
              onChange={handleKycChange}
              onSubmit={() => {}}
              onValidationChange={handleKycValidationChange}
            />
          </Paper>
        );

      case "payment":
        // Check if all prerequisites are met
        if (!isEmailVerified) {
          return (
            <Paper elevation={0} sx={panelSx}>
              <Alert severity="error" sx={alertSx}>
                Email verification is required before payment. Please complete
                the contact step.
              </Alert>
            </Paper>
          );
        }

        if (!isKycVerified) {
          return (
            <Paper elevation={0} sx={panelSx}>
              <Alert severity="error" sx={alertSx}>
                Identity verification is required before payment. Please
                complete the KYC step.
              </Alert>
            </Paper>
          );
        }
        if (!order?.orderId) {
          return (
            <Paper
              elevation={0}
              sx={{
                ...panelSx,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "60vh",
              }}
            >
              <LoadingScreen message="Preparing your order..." />
            </Paper>
          );
        }

        return (
          <>
            {order.orderId ? (
              <Paper elevation={0} sx={panelSx}>
                <PaymentStage order={order} />
              </Paper>
            ) : (
              "isloading"
            )}
          </>
        );

      default:
        return (
          <Paper elevation={0} sx={panelSx}>
            <Alert severity="info" sx={alertSx}>
              Please select items from the catalog to begin checkout.
            </Alert>
          </Paper>
        );
    }
  };

  return <Box>{renderStepContent()}</Box>;
};

const CheckoutPage = () => {
  const location = useLocation();
  const goToStep = useCheckoutFlowStore((state) => state.goToStep);

  const validateCurrentStep = useCheckoutFlowStore(
    (state) => state.validateCurrentStep,
  );

  const { isKycVerified, isEmailVerified, currentStep } =
    useCheckoutFlowStore();

  const { user, isAuthenticated } = useAuthStore();
  // Validate current step whenever user state changes
  useEffect(() => {
    if (!isAuthenticated) return;

    const validation = validateCurrentStep();
    if (!validation.isValid && validation.redirectTo) {
      console.warn(
        `User state changed, redirecting from ${currentStep} to ${validation.redirectTo}: ${validation.reason}`,
      );
      goToStep(validation.redirectTo);
    }
  }, [
    isAuthenticated,
    user,
    isEmailVerified,
    isKycVerified,
    currentStep,
    validateCurrentStep,
    goToStep,
  ]);

  // Set last visit path
  useEffect(() => {
    const fullPath = location.pathname + location.search;
    if (location.pathname === "/checkout") {
      setLastVisitPath(fullPath);
    }
  }, [location]);

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        py: { xs: 3, sm: 3, md: 3 },
        px: { xs: 2, sm: 3, md: 4 },
        overflow: "hidden",
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1, maxWidth: 1400, mx: "auto" }}>
        <CheckoutStepper />

        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={{ xs: 3, lg: 4 }}
          alignItems="flex-start"
        >
          <Box sx={{ width: { xs: "100%", lg: 400 } }}>
            <CartSummary />
          </Box>

          <Box
            sx={{ flex: 1, width: { xs: "100%", lg: "calc(100% - 400px)" } }}
          >
            <StepContent />
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default CheckoutPage;
